import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { appConfig } from 'src/config/appConfig';

export interface OrdinalToken {
  id: string;
  inscriptionNumber: number;
  contentPreviewURI: string;
  contentType: string;
  owner: string;
  collectionSymbol: string;
  collection: {
    name: string;
    imageURI: string;
    symbol: string;
  } | null;
  meta: {
    name: string;
    attributes?: Array<{ trait_type: string; value: string }>;
  } | null;
}

export interface OrdinalCollection {
  symbol: string;
  name: string;
  imageURI: string;
  count: number;
}

export function useOrdinals(ordinalsAddress: string | undefined) {
  const [tokens, setTokens] = useState<OrdinalToken[]>([]);
  const [collections, setCollections] = useState<OrdinalCollection[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCollection, setSelectedCollection] = useState<string | null>(null);

  const fetchTokens = useCallback(async () => {
    if (!ordinalsAddress) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${appConfig.serviceUrl}/ordinals/tokens`, {
        params: {
          ownerAddress: ordinalsAddress,
          limit: 100,
          offset: 0,
        },
      });
      const items: OrdinalToken[] = (response.data.tokens || []).map((item: any) => ({
        id: item.id,
        inscriptionNumber: item.inscriptionNumber,
        contentPreviewURI: item.contentPreviewURI || '',
        contentType: item.contentType || '',
        owner: item.owner,
        collectionSymbol: item.collectionSymbol || '',
        collection: item.collection
          ? {
              name: item.collection.name,
              imageURI: item.collection.imageURI || '',
              symbol: item.collection.symbol,
            }
          : null,
        meta: item.meta
          ? {
              name: item.meta.name || `Inscription #${item.inscriptionNumber}`,
              attributes: item.meta.attributes,
            }
          : { name: `Inscription #${item.inscriptionNumber}` },
      }));

      setTokens(items);

      // Group by collection
      const collectionMap = new Map<string, OrdinalCollection>();
      for (const token of items) {
        const symbol = token.collectionSymbol || '__uncategorized__';
        const existing = collectionMap.get(symbol);
        if (existing) {
          existing.count++;
        } else {
          collectionMap.set(symbol, {
            symbol,
            name: token.collection?.name || 'Uncategorized',
            imageURI: token.collection?.imageURI || '',
            count: 1,
          });
        }
      }
      setCollections(Array.from(collectionMap.values()));
    } catch (err: any) {
      setError(err.message || 'Failed to fetch ordinals');
    } finally {
      setIsLoading(false);
    }
  }, [ordinalsAddress]);

  useEffect(() => {
    fetchTokens();
  }, [fetchTokens]);

  const filteredTokens = selectedCollection
    ? tokens.filter(
        (t) => (t.collectionSymbol || '__uncategorized__') === selectedCollection,
      )
    : tokens;

  return {
    tokens: filteredTokens,
    allTokens: tokens,
    collections,
    isLoading,
    error,
    selectedCollection,
    setSelectedCollection,
    refetch: fetchTokens,
  };
}
